import apiClient from '@/api/client'
import { queryClient } from '@/api/queryClient'
import { buildJsonApiQuery, normalizeData } from '@/utils/jsonapi'

const PREFERENCES_QUERY_KEY = 'user-preferences'

/**
 * How long a scope's preferences are reused. The theme, the home redirect
 * and the dashboards list each read the `global` scope within a second of
 * one another on a cold load; one request now serves all three.
 */
const PREFERENCES_FRESH_MS = 60_000

/**
 * Every preference in `scope`, shared through the app's query client (so a
 * logout or tenant switch clears it with the rest of the session's cache).
 *
 * @param {string} scope - 'global', 'page:<name>' or 'component:<name>'
 * @returns {Promise<Array<{key: string, value: *, scope: string}>>}
 */
export function fetchScopePreferences(scope) {
  return queryClient.fetchQuery({
    queryKey: [PREFERENCES_QUERY_KEY, scope],
    queryFn: async () => {
      const params = buildJsonApiQuery({ filters: { scope } })
      const response = await apiClient.get('/auth/user-preferences/', { params })
      return normalizeData(response.data?.data) || []
    },
    staleTime: PREFERENCES_FRESH_MS,
    retry: false,
  })
}

/** Drops every cached scope, so the next read goes to the server. */
export function forgetUserPreferences() {
  queryClient.removeQueries({ queryKey: [PREFERENCES_QUERY_KEY] })
}

/**
 * Saves preferences, then drops the cached reads so no later reader gets
 * the old values.
 *
 * @param {Array<{key: string, value: *, scope: string}>} preferences
 */
export async function saveUserPreferences(preferences) {
  try {
    return await apiClient.post('/auth/user-preferences/bulk_update/', { preferences })
  } finally {
    forgetUserPreferences()
  }
}
